import {t, Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {isValidEmailAddress} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {PasswordBox} from '../../../libs/shared/ui/src/components/boxedInputs/passwordBox';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {Dialog} from '../../components/common/dialogs/dialog';
import {DialogHeader} from '../../components/common/dialogs/dialogHeader';
import {GridAreaItem} from '../../components/common/grid';
import {InlineBanner, InlineBannerTypesEnum} from '../../components/inlineBanner/inlineBanner';
import {didTeammateSignupWithOauth} from '../../helpers/models/teammateHelpers';
import {TeammateModel} from '../../models/api/teammateModel';
import {postAsync} from '../../services/http/http';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsFieldLine} from '../common/settingsFieldLine';

/*
 * Props.
 */

interface SettingsProfileEmailProps {
  user: TeammateModel;
}

/*
 * Style.
 */

const StyledInlineBanner = styled(InlineBanner)`
  margin-bottom: 20px;
`;

const StyledButtonGroupDiv = styled.div`
  padding: 20px;
  padding-top: 17px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const StyledWrapperDiv = styled.div`
  margin: 20px;
  margin-bottom: 0;
  display: grid;
  grid-template-areas:
    'password'
    '.'
    'checkbox'
    '.'
    'email';
  grid-template-rows: auto 2px auto 24px auto;
`;

const StyledCheckbox = styled(CheckBox)`
  display: inline-flex;
`;

const StyledSemiboldSpan = styled.span`
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

enum ProfileEmailErrorsEnum {
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  ALREADY_USED_EMAIL = 'ALREADY_USED_EMAIL',
  INVALID_EMAIL = 'INVALID_EMAIL',
}

export const SettingsProfileEmail: FC<SettingsProfileEmailProps> = (props) => {
  const {user} = props;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isOauth = didTeammateSignupWithOauth(user);
  const [pendingEmail, setPendingEmail] = useState('');
  const [requestedEmail, setRequestedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requestedChange, setRequestedChange] = useState(false);

  const {showBanner} = useSettingsBanners();
  const [errors, setErrors] = useState<ProfileEmailErrorsEnum[]>([]);
  const canSave = useMemo(
    () => password.trim().length > 0 && pendingEmail.trim().length > 0 && errors.length === 0,
    [errors.length, password, pendingEmail],
  );

  const closeDialogAndResetState = () => {
    setIsDialogOpen(false);
    setPassword('');
    setShowPassword(false);
    setErrors([]);
    setPendingEmail('');
  };

  const PasswordField = showPassword ? TextBox : PasswordBox;

  const requestChangeEmail = async () => {
    try {
      setErrors([]);
      await postAsync(`${user.url}/change_email`, {
        password,
        new_email: pendingEmail,
      });

      setRequestedChange(true);
      setRequestedEmail(pendingEmail);
      closeDialogAndResetState();
    } catch (error) {
      setRequestedEmail('');
      if (error.status === 409) {
        if (error.reason === 'Incorrect password.') {
          setErrors([ProfileEmailErrorsEnum.INVALID_PASSWORD]);
        } else if (error.reason === 'active' || error.reason === 'other_company') {
          setErrors([ProfileEmailErrorsEnum.ALREADY_USED_EMAIL]);
        }
      } else {
        reportToBugsnag(error);
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: error.reason || error.message || (
            <Trans id="settingsprofile-genericerror">An error occurred</Trans>
          ),
        });
      }
    }
  };

  const renderEmailError = () => {
    if (errors.includes(ProfileEmailErrorsEnum.ALREADY_USED_EMAIL)) {
      return <Trans id="settingsprofile-alreadyusedemail">Email already in use</Trans>;
    }

    if (errors.includes(ProfileEmailErrorsEnum.INVALID_EMAIL)) {
      return <Trans id="settingsprofile-invalidemail">Please enter a valid email address</Trans>;
    }

    return null;
  };

  const localize = useLocalize();

  const renderDialog = () => {
    if (!isDialogOpen) {
      return null;
    }

    return (
      <Dialog
        onRequestClose={() => {
          closeDialogAndResetState();
        }}
      >
        <DialogHeader title={<Trans id="settingsprofile-changeemail">Change email</Trans>} />
        <StyledWrapperDiv>
          <GridAreaItem area="password">
            <PasswordField
              defaultValue={password}
              onChange={(value) => {
                setErrors([]);
                setPassword(value);
              }}
              label={<Trans id="settingsprofile-currentpassword">Current password</Trans>}
              hasError={errors.includes(ProfileEmailErrorsEnum.INVALID_PASSWORD)}
              errorMessage={<Trans id="settingsprofile-incorrectpassword">Incorrect password</Trans>}
              placeholder={localize(t('settingsprofileemail-password')`Enter current password`)}
            />
          </GridAreaItem>
          <GridAreaItem area="checkbox">
            <StyledCheckbox
              onChange={() => {
                setShowPassword((p) => !p);
              }}
              isChecked={showPassword}
            >
              <Trans id="settingsprofile-showpassword">Show password</Trans>
            </StyledCheckbox>
          </GridAreaItem>
          <GridAreaItem area="email">
            <TextBox
              label={<Trans id="settingsprofile-newemail">New email address</Trans>}
              defaultValue={pendingEmail}
              onChange={(v) => {
                setErrors([]);
                setPendingEmail(v);
                if (!isValidEmailAddress(v)) {
                  setErrors([ProfileEmailErrorsEnum.INVALID_EMAIL]);
                }
              }}
              hasError={
                errors.includes(ProfileEmailErrorsEnum.ALREADY_USED_EMAIL) ||
                errors.includes(ProfileEmailErrorsEnum.INVALID_EMAIL)
              }
              errorMessage={renderEmailError()}
              placeholder={localize(t('settingsprofileemail-email')`Enter email address`)}
            />
          </GridAreaItem>
        </StyledWrapperDiv>
        <StyledButtonGroupDiv>
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            onClick={() => {
              closeDialogAndResetState();
            }}
          >
            <Trans id="settingsprofile-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} isDisabled={!canSave} onClick={requestChangeEmail}>
            <Trans id="settingsprofile-save">Save</Trans>
          </Button>
        </StyledButtonGroupDiv>
      </Dialog>
    );
  };

  return (
    <>
      {renderDialog()}
      <SettingsFieldLine
        title={<Trans id="settingsprofile-emailaddress">Email address</Trans>}
        subtitle={<Trans id="settingsprofile-changeloginemail">Change your Front login email</Trans>}
        isSubtitleMultiline
        content={
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            size={VisualSizesEnum.MEDIUM}
            onClick={() => {
              setIsDialogOpen(true);
            }}
            isDisabled={isOauth}
          >
            <Trans id="settingsprofile-changeemail">Change email</Trans>
          </Button>
        }
      />
      {isOauth && (
        <StyledInlineBanner
          isNewDesign
          size={VisualSizesEnum.SMALL}
          type={InlineBannerTypesEnum.WARNING}
          title={
            <Trans id="settingsprofile-cannotchangeemail">
              You cannot change your email because you are signed in with Google or Office365.
            </Trans>
          }
          actions={[
            {
              id: 'learn-more',
              onClick: () => {},
              title: <Trans id="settingsprofile-learnmore">Learn more</Trans>,
              iconName: 'externalLinkSmall',
            },
          ]}
        />
      )}
      {requestedChange && (
        <StyledInlineBanner
          isNewDesign
          onClose={() => setRequestedChange(false)}
          size={VisualSizesEnum.SMALL}
          type={InlineBannerTypesEnum.INFO}
          title={
            <Trans id="settingsprofile-sentemailverification">
              We have sent an email to <StyledSemiboldSpan>{requestedEmail}</StyledSemiboldSpan>. We will keep
              using <StyledSemiboldSpan>{user.email}</StyledSemiboldSpan> until you confirm the new address.
            </Trans>
          }
          actions={[
            {
              id: 'learn-more',
              onClick: () => {},
              title: <Trans id="settingsprofile-learnmore">Learn more</Trans>,
              iconName: 'externalLinkSmall',
            },
          ]}
        />
      )}
    </>
  );
};
